import { FC, useEffect, useState } from "react"
import { Guid } from "../../../api/baseApi"
import { CenterModal } from "../sideModal/Modal"
import { Button } from "../Button"
import { formatDate, formatTime } from "../../../services/dateService"
import Tabs, { TabElement, TabNavigation, TabNavigationElement, TabsContainer } from "../tab/Tab"
import { MediaAnalyzeProfile, MediaAnalyzeStatus, MediaTranscode, getIsUseOriginal, getMediaAnalyzeProfile, getMediaTranscodes, postMediaTranscodeRequest, postUseOriginal, postUseTranscode } from "../../../api/mediaApi"
import FormCheckbox from "../formRow/FormCheckbox"
import { converFromBytes, formatDuration } from "../../../services/common"

interface MediaTranscodeModalProps {
    id:Guid,
    isShow: boolean,
    closeHandler: () => void,
    loadingHandler: (isLoading: boolean) => void
}

type SettingsTab = "MediaInfo" | "MediaTranscodes";

const MediaTranscodeModal:FC<MediaTranscodeModalProps> = ({id, isShow, closeHandler, loadingHandler}) => {

    let [mediaAnalyzeProfile, setMediaAnalyzeProfile] = useState<MediaAnalyzeProfile | null>(null);
    let [mediaTranscodesProfiles, setMediaTranscodesProfile] = useState<MediaTranscode[]>([]);
    let [isUseTranscode, setIsUseTranscode] = useState(false);
    let [activeTab, setActiveTab] = useState<SettingsTab>("MediaInfo");
    const checkActive = (tab: SettingsTab) =>  tab === activeTab;

    useEffect(()=>{
        async function fetchData(){
            loadingHandler(true);
            await Promise.all([updateProfiles(id), checkUseOriginal(id), updateTranscodeProfiles(id)])
            loadingHandler(false);
        }

        isShow && fetchData()
    },[id])

    useEffect(()=>{

        if(activeTab === "MediaTranscodes") {
            updateTranscodeProfiles(id)
        }

    },[activeTab])

    const updateProfiles = async (id:Guid) => {
        var getProfilesResult = await getMediaAnalyzeProfile(id);
        if(getProfilesResult.type == "succes"){
            setMediaAnalyzeProfile(getProfilesResult.data.data)
        }
    }

    const updateTranscodeProfiles = async (id:Guid) => {
        var getProfilesResult = await getMediaTranscodes(id);
        if(getProfilesResult.type == "succes"){
            setMediaTranscodesProfile(getProfilesResult.data.items)
        }
    }

    const checkUseOriginal = async (id:Guid) => {
        var getProfilesResult = await getIsUseOriginal(id);
        if(getProfilesResult.type == "succes"){
            setIsUseTranscode(!getProfilesResult.data.data)
        }
    }

    const toggleHandler = async (val: boolean) => {
        setIsUseTranscode(val)
        if(val) {
            await postUseTranscode(id);
        } else {
            await postUseOriginal(id)
        }
    }


    const sendTranscodeRequest = async (id:Guid, profileId:Guid) => {
        await postMediaTranscodeRequest(id, profileId);
        await updateTranscodeProfiles(id)
    }

    const onRequestClose = () => {
        setMediaTranscodesProfile([]);
        setMediaAnalyzeProfile(null)
        closeHandler();
    }

    const renderStatus = (status:MediaAnalyzeStatus) => {
        switch(status){
            case MediaAnalyzeStatus.Requested : return <span>Requested</span>
            case MediaAnalyzeStatus.Posted : return <span>Posted</span>
            case MediaAnalyzeStatus.Pending : return <span>Pending</span>
            case MediaAnalyzeStatus.Running : return <span>Running</span>
            case MediaAnalyzeStatus.Success : return <span>Success</span>
            case MediaAnalyzeStatus.Failed : return <span>Failed</span>
            case MediaAnalyzeStatus.Cancelled : return <span>Cancelled</span>
            case MediaAnalyzeStatus.InternalError : return <span>Internal Error</span>
            case MediaAnalyzeStatus.Complete : return <span>Complete</span>
        }
    }

    const renderMediaAnalyzeProfile = (it: MediaAnalyzeProfile) => {
        return <tr key={it.jobId}>
            <td>
                <p>Requested Time: {formatDate(it.requestedTime)} <br/> {formatTime(it.requestedTime)}</p>
                <p>Last Update Time: {formatDate(it.lastUpdateTime)} <br/> {formatTime(it.lastUpdateTime)}</p>
                <p>Complete Time: {formatDate(it.completeTime)} <br/> {formatTime(it.completeTime)}</p>
            </td>
            <td>{renderStatus(it.status)}</td>
            <td>
                <p>Kind: {it.mediaInfoContainerKind}</p>
                <p>Duration: {formatDuration(it.mediaInfoContainerDuration * 1000)}</p>
                <p style={{"whiteSpace" : "nowrap"}}>Bitrate: {converFromBytes(it.mediaInfoContainerBitrate, "KB").toFixed(2)} KB/sec</p>
                <p>Size: {converFromBytes(it.mediaInfoContainerSize, "MB").toFixed(2)} MB</p>
            </td>
            <td>
                <p>Codec: {it.audioCodec}</p>
                <p>Channels: {it.audioChannels}</p>
                <p>Channel Order: {it.audioChannelOrder}</p>
                <p>Sample Rate: {it.audioSampleRate} Hz</p>
                <p>Duration: {formatDuration(it.audioDuration * 1000)}</p>
                <p style={{"whiteSpace" : "nowrap"}}>Bitrate: {converFromBytes(it.audioBitrate, "KB").toFixed(2)} KB/sec</p>
            </td>
            <td>
                <p>Codec: {it.videoCodec}</p>
                <p>Frame Rate: {it.videoFrameRate} FPS</p>
                <p>Width: {it.videoWidth}</p>
                <p>Height: {it.videoHeight}</p>
                <p>Duration: {formatDuration(it.videoDuration * 1000)}</p>
                <p style={{"whiteSpace" : "nowrap"}}>Bitrate: {converFromBytes(it.videoBitrate, "KB").toFixed(2)} KB/sec</p>
            </td>
            <td>
                <p style={{"whiteSpace" : "nowrap"}}>Measured: {it.loudnessMeasured} dB</p>
                <p>Range: {it.loudnessRange}</p>
                <p>Gating Mode: {it.loudnessGatingMode}</p>
                <p style={{"whiteSpace" : "nowrap"}}>Sample Peak: {it.loudnessSamplePeak} dB</p>
                <p style={{"whiteSpace" : "nowrap"}}>True Peak: {it.loudnessTruePeak} dB</p>
            </td>
            <td>{it.jobId}</td>
            <td>{it.dolbyOutputPath}</td>
        </tr>
    }

    const renderMediaTranscodes = (transcodes: MediaTranscode[]) => {
        return transcodes.map( it => {
            return <tr key={it.profileId}>
                <td>
                    <p>{formatDate(it.requestedTime)} {formatTime(it.requestedTime)}</p>
                </td>
                <td>
                    <p>{formatDate(it.lastUpdateTime)} {formatTime(it.lastUpdateTime)}</p>
                </td>
                <td>
                    <p>{formatDate(it.completeTime)} {formatTime(it.completeTime)}</p>
                </td>
                <td>{renderStatus(it.status)}</td>
                <td>{it.profileName}</td>
                <td>
                    <div className="d-flex justify-content-end">
                        {it.canBeRequested && <Button color="green" label="Request Transcode" onClick={() => sendTranscodeRequest(id, it.profileId) }/>}
                        {it.downloadLink && <a href={`${it.downloadLink}`} download>Download</a>}
                    </div>
                </td>
            </tr>
        })
    }
    
    return (
        <CenterModal title="Media Transcode" show={isShow} onRequestClose={onRequestClose}>
            <Tabs>
                <TabNavigation>
                    <TabNavigationElement 
                        title="Info" 
                        isActive={checkActive("MediaInfo")} 
                        onClick={() => { setActiveTab("MediaInfo") }} 
                    />
                    <TabNavigationElement 
                        title="Transcodes" 
                        isActive={checkActive("MediaTranscodes")} 
                        onClick={() => { setActiveTab("MediaTranscodes") }} 
                    />
                    
                </TabNavigation>
                <TabsContainer>
                    <TabElement isActive={checkActive("MediaInfo")}>
                        {!!mediaAnalyzeProfile ? <table className="table report-table">
                            <thead>
                                <tr>
                                    <td>Time</td>
                                    <td>Status</td>
                                    <td>Media Info Container</td>
                                    <td>Audio</td>
                                    <td>Video</td>
                                    <td>Loudness</td>
                                    <td>JobId</td>
                                    <td>Dolby Output Path</td>
                                </tr>
                            </thead>
                            <tbody>
                                {renderMediaAnalyzeProfile(mediaAnalyzeProfile)}
                            </tbody>
                        </table> : <p>No Info</p>}
                    </TabElement>
                    <TabElement isActive={checkActive("MediaTranscodes")}>
                        <>
                        <div className="d-flex align-items-center w-100">
                            <FormCheckbox id="use-trancode" checked={isUseTranscode} onChange={toggleHandler} />
                            <span className="status-block">
                                {isUseTranscode ? <span className="current-status online">&nbsp;Use Transcoded</span> : <span className="current-status offline">&nbsp;Use Transcoded</span>}
                            </span>
                        </div>
                        {mediaTranscodesProfiles.length ? <table className="table report-table table-divider">
                            <thead>
                                <tr>
                                    <td>Requested </td>
                                    <td>Last Update</td>
                                    <td>Complete </td>
                                    <td>Status</td>
                                    <td>Name</td>
                                    <td></td>
                                </tr>
                            </thead>
                            <tbody>
                                {renderMediaTranscodes(mediaTranscodesProfiles)}
                            </tbody>
                        </table> : <p>No Elements</p>}
                        </>
                    </TabElement>
                </TabsContainer>
            </Tabs>
        </CenterModal> 
    )
}

export default MediaTranscodeModal